// İSG uzmanı portalı — İş İzni (Permits) erişim sondası (route guard UX).
//
// Asıl yetki backend'dedir: her İş İzni ucu `[ModuleAction("permits", …)]` taşır; grant'ı
// olmayan principal → 403 (api-contract §14). Bu sonda yalnız UX görünürlüğüdür — hafif bir
// liste probu (`GET /permits?page=1&pageSize=1`) çeker:
//   - 2xx  → erişim var (granted) → İş İzni kabuğu + menü görünür
//   - 403  → yetki yok (forbidden) → gizli menü + 403 sayfası
//   - diğer (404/503/ağ) → servis/veri kullanılamıyor (unavailable) → yeniden dene
//
// Taşeron (§13) / Ayarlar (§10) / Ziyaretçi (§11) sondalarıyla aynı desendir; iş modülü
// olarak AYRIDIR. Gerçek modül Value = `permits` (küçük harf; §14) — probun bağladığı uç
// `list` aksiyonudur; yetkisiz principal buton/route göremez.

import { ApiError, permitsApi } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { permitsKeys } from './permitsKeys';

export type PermitsAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface PermitsAccess {
  status: PermitsAccessStatus;
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

/**
 * İş İzni erişimini backend sondasıyla belirler. Oturum açık (RequireAuth altı) varsayılır.
 * 4xx'te retry yok (anında sonuç); 403 (yetki yok) ≠ 404/503 (servis/veri) ayrımı korunur.
 */
export function usePermitsAccess(): PermitsAccess {
  const query = useQuery({
    queryKey: permitsKeys.access(),
    queryFn: () => permitsApi.listPermits({ page: 1, pageSize: 1 }),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: PermitsAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, refetch: () => void query.refetch() };
}
